import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { RefObject, useRef } from "react";
import { LogoClipPath } from "../LogoClipPath";
import {
  animationContainerSx,
  backgroundSx,
  fullVideoContainerSx,
  heroContainerSx,
  maskedClipContainerSx,
  maskedVideoContainerSx,
  maskedVideoWrapperSx,
  textOverlaySx,
  textTitleSx,
  fullVideoSx,
  videoSx,
  getPosterSx,
} from "./HeroSection.sx";
import {
  heroTitle,
  heroSubtitle,
  posterSources,
  VideoBreakpoint,
} from "./HeroSection.config";
import { useHeroAnimation } from "./useHeroAnimation";
import { useResponsiveVideo } from "./useResponsiveVideo";

export interface HeroSectionProps {
  scrollTriggerRef?: RefObject<HTMLDivElement | null>;
  aboutSectionRef?: RefObject<HTMLDivElement | null>;
}

export const HeroSection = ({
  scrollTriggerRef,
  aboutSectionRef,
}: HeroSectionProps) => {
  const heroRef = useRef<HTMLDivElement>(null);
  const fullVideoRef = useRef<HTMLDivElement>(null);
  const maskedWrapperRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const { videoSrc, isVideoPlaying, handleVideoPlaying } = useResponsiveVideo({
    videoRef,
  });

  useHeroAnimation({
    heroRef,
    fullVideoRef,
    maskedWrapperRef,
    textRef,
    scrollTriggerRef,
    aboutSectionRef,
  });

  return (
    <Box ref={heroRef} sx={heroContainerSx}>
      <LogoClipPath />

      <Box sx={animationContainerSx}>
        <Box sx={backgroundSx} />

        <Box ref={fullVideoRef} sx={fullVideoContainerSx}>
          {/* Poster images - CSS controls display, JS controls opacity */}
          <Box
            component="img"
            src={posterSources[VideoBreakpoint.Mobile]}
            alt=""
            sx={getPosterSx(VideoBreakpoint.Mobile, isVideoPlaying)}
          />
          <Box
            component="img"
            src={posterSources[VideoBreakpoint.Tablet]}
            alt=""
            sx={getPosterSx(VideoBreakpoint.Tablet, isVideoPlaying)}
          />
          <Box
            component="img"
            src={posterSources[VideoBreakpoint.Desktop]}
            alt=""
            sx={getPosterSx(VideoBreakpoint.Desktop, isVideoPlaying)}
          />
          {/* Single video - renders after mount with correct source */}
          {videoSrc && (
            <Box
              key={videoSrc}
              component="video"
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              onPlaying={handleVideoPlaying}
              sx={fullVideoSx}
            >
              <source src={videoSrc} type="video/mp4" />
            </Box>
          )}
        </Box>

        <Box ref={textRef} sx={textOverlaySx}>
          <Typography variant="h2" component="h1" sx={textTitleSx}>
            {heroTitle}
          </Typography>
          <Typography variant="h2" component="span" sx={textTitleSx}>
            {heroSubtitle}
          </Typography>
        </Box>
      </Box>

      <Box sx={maskedClipContainerSx}>
        <Box ref={maskedWrapperRef} sx={maskedVideoWrapperSx}>
          <Box sx={maskedVideoContainerSx}>
            {/* Single video - matches fullVideo source */}
            {videoSrc && (
              <Box
                key={videoSrc}
                component="video"
                autoPlay
                muted
                loop
                playsInline
                sx={videoSx}
              >
                <source src={videoSrc} type="video/mp4" />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HeroSection;
